import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AdminClaims, AdminRole } from '@/admin/auth/admin-claims';
import { areClaimsExpired, roleFromClaims } from '@/admin/auth/admin-claims';
import { decodeAdminJwt } from '@/admin/auth/decode-admin-jwt';

/**
 * Zustand slice for admin authentication state. Persisted to localStorage so
 * a page refresh doesn't kick the user to /admin/login.
 *
 * The token itself is trusted — signature verification is the backend's job.
 * We only read claims to drive routing and UI gating.
 *
 * PROD-1781: `role` is PERSISTED, so the expiry check in `roleFromClaims`
 * would have been decorative on its own — a role computed while the token was
 * live would be rehydrated verbatim forever after it died. `onRehydrateStorage`
 * below re-derives it from the stored claims on every boot, so an expired
 * session comes back as `none` (and drops its tokens) instead of as whatever
 * it used to be.
 */

interface AdminAuthState {
  accessToken: string | null;
  refreshToken: string | null;
  claims: AdminClaims | null;
  role: AdminRole;

  setTokens: (access: string, refresh?: string | null) => void;
  clear: () => void;
}

const STORAGE_KEY = 'scrips-admin-auth';

export const useAdminAuth = create<AdminAuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      claims: null,
      role: 'none',

      setTokens: (access, refresh) => {
        const claims = decodeAdminJwt(access);
        set({
          accessToken: access,
          refreshToken: refresh ?? null,
          claims,
          role: roleFromClaims(claims),
        });
      },

      clear: () =>
        set({
          accessToken: null,
          refreshToken: null,
          claims: null,
          role: 'none',
        }),
    }),
    {
      name: STORAGE_KEY,
      /**
       * Re-derive `role` from the rehydrated claims (PROD-1781). If the stored
       * token has expired, drop it as well as the role — leaving a dead
       * `accessToken` in place would keep `ProtectedRoute` past its
       * `if (!accessToken)` check and bounce the user to /admin/403 instead of
       * /admin/login, which reads as "forbidden" when the truth is
       * "logged out".
       */
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        if (areClaimsExpired(state.claims)) {
          state.accessToken = null;
          state.refreshToken = null;
          state.claims = null;
          state.role = 'none';
          return;
        }
        state.role = roleFromClaims(state.claims);
      },
    },
  ),
);

/** Convenience hook selectors to avoid re-renders on unrelated changes. */
export const useAdminRole = () => useAdminAuth((s) => s.role);
export const useAdminClaims = () => useAdminAuth((s) => s.claims);
export const useAdminToken = () => useAdminAuth((s) => s.accessToken);
